import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from './AuthContext';

const LEGACY_SELECTED_COLLECTION_KEY = 'selectedCollection';

const CollectionContext = createContext(null);

export const CollectionProvider = ({ children }) => {
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollectionState] = useState('all');
  const { user } = useAuth();

  const setSelectedCollection = (val) => {
    setSelectedCollectionState(val != null && val !== '' ? String(val) : 'all');
  };

  useEffect(() => {
    try {
      localStorage.removeItem(LEGACY_SELECTED_COLLECTION_KEY);
    } catch (_) {
      /* ignore */
    }
  }, []);

  const fetchCollections = useCallback(async () => {
    try {
      const response = await axios.get('/api/collections/');
      const raw = response.data;
      const list = Array.isArray(raw) ? raw : [];
      setCollections(list);
      setSelectedCollectionState((prev) => {
        if (prev === 'all' || prev == null || prev === '') return 'all';
        const sid = String(prev);
        const ok = list.some((c) => c != null && String(c.id) === sid);
        if (ok) return sid;
        return 'all';
      });
    } catch (err) {
      console.error('Failed to fetch collections:', err);
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchCollections();
    }
  }, [user, fetchCollections]);

  return (
    <CollectionContext.Provider
      value={{
        collections,
        selectedCollection: selectedCollection ? selectedCollection.toString() : 'all',
        setSelectedCollection,
        refreshCollections: fetchCollections,
      }}
    >
      {children}
    </CollectionContext.Provider>
  );
};

export const useCollections = () => useContext(CollectionContext);

/**
 * Deep links carry `?collection_id=N` (e.g. from the paper page). Apply it to the
 * global selection once, on mount. Returns true while that request is still
 * pending, so a page's own URL-sync effect does not strip the param before the
 * selection has switched over.
 */
export const useCollectionFromUrl = () => {
  const [searchParams] = useSearchParams();
  const { selectedCollection, setSelectedCollection } = useCollections();
  const requestedRef = useRef(searchParams.get('collection_id'));
  useEffect(() => {
    const want = requestedRef.current;
    if (want && /^\d+$/.test(want) && want !== selectedCollection) setSelectedCollection(want);
    // Once only: later changes come from the user's own collection picker.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const want = requestedRef.current;
  const appliedRef = useRef(!(want && /^\d+$/.test(want)));
  if (!appliedRef.current && want === selectedCollection) appliedRef.current = true;
  return !appliedRef.current;
};
